import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, FormGroup } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import MaskedInput from 'react-text-mask';
import { MASK_CPF, MASK_CEL_9 } from '~/constants';
import { validarLinkCorretor, cadastrarLeadLinkCorretor, buscarDadosCadastroPublico } from '~/service/Service';

const logoSetpar = 'https://repositorio.setcorp.com.br/imagens/logo/setpar-logo.png';

const validate = (values, { semToken }) => {
  const errors = {};
  if (!values.nome || values.nome.trim().length < 3)
    errors.nome = !values.nome ? 'Nome é obrigatório' : 'Nome deve ter no mínimo 3 caracteres';
  if (!values.telefone || values.telefone.replace(/\D/g, '').length < 10)
    errors.telefone = !values.telefone ? 'Telefone é obrigatório' : 'Telefone inválido';
  if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
    errors.email = 'E-mail inválido';
  if (values.cpf && values.cpf.replace(/\D/g, '').length !== 11)
    errors.cpf = 'CPF inválido';
  if (semToken && !values.corretor)
    errors.corretor = 'Selecione um corretor';
  return errors;
};

export default function CadastroLeadPublico({ match }) {
  const token = match.params.token || null;
  const semToken = !token;

  const [validando, setValidando] = useState(true);
  const [tokenInvalido, setTokenInvalido] = useState(false);
  const [dados, setDados] = useState(null);
  const [corretores, setCorretores] = useState([]);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (token) {
      validarLinkCorretor(token)
        .then(response => {
          setDados(response);
          setValidando(false);
        })
        .catch(() => {
          setTokenInvalido(true);
          setValidando(false);
        });
    } else {
      buscarDadosCadastroPublico()
        .then(response => {
          setDados({
            empreendimento: response.empreendimento,
            logo: response.logo,
            codigo_familia: response.codigo_familia,
          });
          setCorretores(response.corretores || []);
          setValidando(false);
        })
        .catch(() => {
          setTokenInvalido(true);
          setValidando(false);
        });
    }
  }, [token]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setErro('');
      const payload = {
        nome: values.nome.trim(),
        telefone: values.telefone.replace(/\D/g, ''),
        email: values.email || null,
        cpf: values.cpf ? values.cpf.replace(/\D/g, '') : null,
      };

      if (token) {
        payload.token = token;
      } else {
        payload.codigo_corretor = parseInt(values.corretor, 10);
        payload.codigo_familia = dados.codigo_familia;
      }

      await cadastrarLeadLinkCorretor(payload);
      setSucesso(true);
    } catch (error) {
      setErro((error && error.message) || 'Erro ao realizar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  if (validando) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border" role="status"><span className="sr-only">Carregando...</span></div>
      </Container>
    );
  }

  if (tokenInvalido) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <img src={logoSetpar} alt="Setpar" style={{ width: 160, marginBottom: 24 }} />
        <Alert variant="danger">{semToken ? 'Nenhuma campanha ativa no momento.' : 'Link inválido ou expirado.'}</Alert>
      </Container>
    );
  }

  const logoSrc = dados && dados.logo ? dados.logo : logoSetpar;

  if (sucesso) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <img src={logoSrc} alt={dados.empreendimento} style={{ maxWidth: 250, marginBottom: 24 }} />
        <Card style={{ width: '100%', maxWidth: 480 }}>
          <Card.Body className="text-center py-5">
            <h4>Cadastro realizado com sucesso!</h4>
            <p className="text-muted mt-3">Entraremos em contato em breve.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="d-flex flex-column justify-content-center align-items-center py-4" style={{ minHeight: '100vh' }}>
      <img src={logoSrc} alt={dados.empreendimento} style={{ maxWidth: 250, marginBottom: 24 }} />
      <Card style={{ width: '100%', maxWidth: 480 }}>
        <Card.Body>
          <h4 className="mb-1">{dados.empreendimento}</h4>
          {!semToken && <p className="text-muted mb-4">Corretor: {dados.corretor}</p>}

          {erro && <Alert variant="danger" className="mb-3">{erro}</Alert>}

          <Formik
            initialValues={{ nome: '', telefone: '', email: '', cpf: '', corretor: '' }}
            validate={values => validate(values, { semToken })}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                {semToken && (
                  <FormGroup className="mb-3">
                    <Form.Label>Corretor <span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control
                      as="select"
                      name="corretor"
                      value={values.corretor}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className={touched.corretor && errors.corretor ? 'is-invalid' : ''}
                    >
                      <option value="">Selecione um corretor...</option>
                      {corretores.map(c => (
                        <option key={c.codigo_usuario} value={c.codigo_usuario}>{c.nome}</option>
                      ))}
                    </Form.Control>
                    {touched.corretor && errors.corretor && (
                      <div className="text-danger small mt-1">{errors.corretor}</div>
                    )}
                  </FormGroup>
                )}

                <FormGroup className="mb-3">
                  <Form.Label>Nome <span style={{ color: 'red' }}>*</span></Form.Label>
                  <Field
                    type="text"
                    name="nome"
                    className={`form-control${touched.nome && errors.nome ? ' is-invalid' : ''}`}
                    placeholder="Seu nome completo"
                    disabled={isSubmitting}
                  />
                  {touched.nome && errors.nome && (
                    <div className="text-danger small mt-1">{errors.nome}</div>
                  )}
                </FormGroup>

                <FormGroup className="mb-3">
                  <Form.Label>Telefone <span style={{ color: 'red' }}>*</span></Form.Label>
                  <MaskedInput
                    mask={MASK_CEL_9}
                    guide={false}
                    name="telefone"
                    className={`form-control${touched.telefone && errors.telefone ? ' is-invalid' : ''}`}
                    value={values.telefone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="(00) 00000-0000"
                    disabled={isSubmitting}
                  />
                  {touched.telefone && errors.telefone && (
                    <div className="text-danger small mt-1">{errors.telefone}</div>
                  )}
                </FormGroup>

                <FormGroup className="mb-3">
                  <Form.Label>E-mail <small className="text-muted">(opcional)</small></Form.Label>
                  <Field
                    type="email"
                    name="email"
                    className={`form-control${touched.email && errors.email ? ' is-invalid' : ''}`}
                    placeholder="seu@email.com"
                    disabled={isSubmitting}
                  />
                  {touched.email && errors.email && (
                    <div className="text-danger small mt-1">{errors.email}</div>
                  )}
                </FormGroup>

                <FormGroup className="mb-4">
                  <Form.Label>CPF <small className="text-muted">(opcional)</small></Form.Label>
                  <MaskedInput
                    mask={MASK_CPF}
                    guide={false}
                    name="cpf"
                    className={`form-control${touched.cpf && errors.cpf ? ' is-invalid' : ''}`}
                    value={values.cpf}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="000.000.000-00"
                    disabled={isSubmitting}
                  />
                  {touched.cpf && errors.cpf && (
                    <div className="text-danger small mt-1">{errors.cpf}</div>
                  )}
                </FormGroup>

                <Button
                  type="submit"
                  variant="primary"
                  block
                  disabled={isSubmitting || (semToken && !values.corretor)}
                  style={{ background: '#419AEF', borderColor: '#419AEF' }}
                >
                  {isSubmitting ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Cadastrar'}
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
}
